import { useCallback, useEffect, useMemo, useState } from "react";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { useSearchParams } from "react-router-dom";
import { UserCog, Plus, Upload, Webhook, AlertTriangle, RefreshCw, CheckCircle2, Circle, Copy, Check as CheckIcon, ShieldCheck } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage, getViewingOrgId } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { DetailFields, DetailSection, RecordActions, type DetailField } from "../components/RecordView";
import {
  EmptyState, Field, ModalHeader, ModalOverlay, StatusPill, dangerBtn, fmtDateTime, fmtDateOnly, toDateInput, todayInput, fromInput, input, relativeDue, secondaryBtn, textarea,
  type AttentionItem,
} from "./resilience/shared";
import { PlaybookBar } from "../components/PlaybookBar";

// Joiners and leavers — every start, move and departure with the checklist that goes with it
// (backend/src/routes/people.routes.ts, lib/peopleLifecycle.ts). Automatic items are ticked by
// the platform from its own records and cannot be ticked by hand; a box someone ticks is not
// evidence that it happened. Every write needs org:admin.

type Kind = "joiner" | "mover" | "leaver";

interface LifecycleItem {
  key: string; title: string; detail: string; auto: boolean; required: boolean; done: boolean;
  doneAt: string | null; doneBy: string | null; note: string | null;
  evidence?: string | null; manualBecause?: string | null;
}
interface LifecycleEvent {
  id: string; displayId: number | null; userId: string | null; personName: string; personEmail: string | null;
  kind: Kind; source: string; effectiveAt: string; status: "open" | "completed" | "cancelled";
  detail: { fromDepartment?: string | null; toDepartment?: string | null; fromJobTitle?: string | null; toJobTitle?: string | null; fromSite?: string | null; toSite?: string | null; reason?: string | null; cancelledReason?: string | null } | null;
  items: LifecycleItem[]; completedAt: string | null;
  // A person's name, "Checked automatically" when the platform closed it, or null while not completed.
  completedBy?: string | null;
  user: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
}
interface Member { id: string; displayName: string | null; email: string; isActive: boolean }
interface Connections {
  hris: boolean; hrisEnabledAt: string | null; m365Security: boolean; intune: boolean; directorySync: boolean; directoryLastSyncAt: string | null;
}
interface PeopleView { events: LifecycleEvent[]; attention: AttentionItem[]; members: Member[]; sources: Record<string, string>; connections: Connections }

const DAY = 86_400_000;
const ref = (e: { displayId: number | null }) => (e.displayId != null ? `JML-${String(e.displayId).padStart(3, "0")}` : "JML");
const KIND_LABEL: Record<Kind, string> = { joiner: "Joiner", mover: "Mover", leaver: "Leaver" };
const KIND_STYLE: Record<Kind, { bg: string; color: string }> = {
  joiner: { bg: theme.color.successBg, color: theme.color.successText },
  mover:  { bg: theme.color.accentBg,  color: theme.color.accentText },
  leaver: { bg: theme.color.warningBg, color: theme.color.warningText },
};
const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

function KindPill({ kind }: { kind: Kind }) {
  const s = KIND_STYLE[kind] ?? { bg: theme.color.panelAlt, color: theme.color.muted };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{KIND_LABEL[kind] ?? kind}</span>;
}

function OnOff({ on, onLabel = "On", offLabel = "Off" }: { on: boolean; onLabel?: string; offLabel?: string }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, whiteSpace: "nowrap", background: on ? theme.color.successBg : theme.color.panelAlt, color: on ? theme.color.successText : theme.color.muted }}>
      {on ? onLabel : offLabel}
    </span>
  );
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const progress = (items: LifecycleItem[]) => {
  const req = items.filter((i) => i.required);
  return { done: req.filter((i) => i.done).length, total: req.length };
};

const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };
const iconBtn: React.CSSProperties = { ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 };

export function PeopleLifecyclePage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const can = (p: string) => !!user?.permissions?.includes(p);
  const canEdit = can("people:write");
  // Deactivating an account stays an administrator's; the HR webhook secret is an integration.
  const canRevokeAccess = can("org:admin");
  const canSetUpWebhook = can("integrations:admin");
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<PeopleView | null>(null);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [kindFilter, setKindFilter] = useState<"" | Kind>("");
  const [recording, setRecording] = useState(false);
  const [importing, setImporting] = useState(false);
  const [amending, setAmending] = useState<LifecycleEvent | null>(null);
  const [webhook, setWebhook] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<PeopleView>("/people")); }
    catch (e) { setError(apiErrorMessage(e, "Could not load joiners and leavers.")); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openId = searchParams.get("event");
  const openEvent = openId && data ? data.events.find((e) => e.id === openId) ?? null : null;
  const openEventById = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("event", id); else next.delete("event");
    setSearchParams(next, { replace: true });
  };

  const tiles = useMemo(() => {
    if (!data) return null;
    const since = Date.now() - 90 * DAY;
    const recent = (k: Kind) => data.events.filter((e) => e.kind === k && e.status !== "cancelled" && new Date(e.effectiveAt).getTime() >= since && new Date(e.effectiveAt).getTime() <= Date.now()).length;
    return {
      open: data.events.filter((e) => e.status === "open").length,
      leaversOutstanding: data.events.filter((e) => e.status === "open" && e.kind === "leaver" && e.items.some((i) => i.required && !i.done)).length,
      joiners: recent("joiner"),
      leavers: recent("leaver"),
    };
  }, [data]);

  const events = useMemo(() => (data?.events ?? []).filter((e) => (showAll || e.status === "open") && (!kindFilter || e.kind === kindFilter)), [data, showAll, kindFilter]);

  const orgId = getViewingOrgId() ?? user?.organizationId ?? null;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <UserCog size={20} /> Joiners and leavers
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 760 }}>
            Every start, move and departure, with the checklist that goes with it. Steps the platform
            can see for itself tick themselves{iso27001Citation(enrolled, "A.5.11, A.5.18, A.6.1, A.6.5")}.
          </p>
          <PlaybookBar ids={["access-and-identity"]} />
          <PolicySaysChip keys={["access.leaver_revocation_hours"]} />
        </div>
        {canEdit && data && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setRecording(true)}>
            <Plus size={14} /> Record a change
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}

      {!data || !tiles ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Tile label="Open checklists" value={tiles.open} sub="joiners, movers and leavers" />
            <Tile label="Leavers outstanding" value={tiles.leaversOutstanding} sub="with required steps not done" />
            <Tile label="Joiners" value={tiles.joiners} sub="in the last 90 days" />
            <Tile label="Leavers" value={tiles.leavers} sub="in the last 90 days" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            <div style={{ ...card, padding: 0 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...text.sectionTitle, margin: 0 }}>
                <AlertTriangle size={15} /> Needs attention
                <span style={{ ...text.hint, fontWeight: 400 }}>{data.attention.length}</span>
              </div>
              {data.attention.length === 0 ? (
                <div style={{ padding: 20, ...text.body, color: theme.color.muted }}>Nothing outstanding.</div>
              ) : data.attention.map((a) => (
                <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={text.itemTitle}>{a.title}</div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                    </div>
                    {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                  </div>
                </PortalLink>
              ))}
            </div>

            <div style={{ ...card, padding: 18 }}>
              <div style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Where events come from</div>
              <div style={{ display: "grid", gap: 10, fontSize: text.body.fontSize }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ color: theme.color.text }}>Directory sync</span>
                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <OnOff on={data.connections.directorySync} />
                    <PortalLink to="/team" style={{ fontSize: text.hint.fontSize, color: "var(--c-accentText)" }}>Team</PortalLink>
                  </span>
                </div>
                {data.connections.directorySync && <div style={{ ...text.meta, marginTop: -6 }}>Last sync {data.connections.directoryLastSyncAt ? fmtDateTime(data.connections.directoryLastSyncAt) : "not yet run"}. Department and office moves and disabled accounts become events.</div>}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ color: theme.color.text }}>HR system webhook</span>
                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <OnOff on={data.connections.hris} />
                    {canSetUpWebhook && <button type="button" style={{ ...secondaryBtn, padding: "3px 10px" }} onClick={() => setWebhook(true)}>Set up</button>}
                  </span>
                </div>
                {canEdit && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" style={iconBtn} onClick={() => setImporting(true)}><Upload size={13} /> Import from HR (CSV)</button>
                    <button type="button" style={iconBtn} onClick={() => setRecording(true)}><Plus size={13} /> Record by hand</button>
                  </div>
                )}
                <div style={{ borderTop: "1px solid var(--c-border)", paddingTop: 10, ...text.hint, lineHeight: 1.55 }}>
                  The automatic checks read Microsoft 365 Security (directory account disabled, MFA
                  registered) and Intune (devices enrolled and returned). Without them those steps
                  are confirmed by hand.
                  <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>Microsoft 365 Security <OnOff on={data.connections.m365Security} onLabel="Connected" offLabel="Not connected" /></span>
                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>Intune <OnOff on={data.connections.intune} onLabel="Connected" offLabel="Not connected" /></span>
                  </div>
                  <div style={{ marginTop: 8 }}><PortalLink to="/connections" style={{ fontSize: text.hint.fontSize, color: "var(--c-accentText)" }}>Connections</PortalLink></div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              {([["open", false], ["all", true]] as const).map(([label, all]) => (
                <button key={label} type="button" onClick={() => setShowAll(all)} style={{
                  ...secondaryBtn, borderColor: showAll === all ? theme.color.accent : theme.color.borderStrong,
                  background: showAll === all ? "var(--c-accentBg)" : "transparent", color: showAll === all ? "var(--c-accentText)" : theme.color.muted, fontWeight: showAll === all ? 600 : 400,
                }}>{label === "open" ? "Open" : "All"}</button>
              ))}
              <select style={{ ...input, width: 160 }} value={kindFilter} onChange={(e) => setKindFilter(e.target.value as "" | Kind)}>
                <option value="">Every change</option>
                <option value="joiner">Joiners</option>
                <option value="mover">Movers</option>
                <option value="leaver">Leavers</option>
              </select>
            </div>

            {events.length === 0 ? (
              <div style={card}>
                <EmptyState
                  title={data.events.length === 0 ? "No joiners or leavers recorded" : "Nothing matches"}
                  detail={data.events.length === 0
                    ? "Events arrive from directory sync, your HR system, an HR export, an invitation being accepted or a member being deactivated — or record one by hand."
                    : showAll ? "No event of this kind." : "No open checklist of this kind. Show all to see completed and cancelled ones."}
                  action={canEdit && data.events.length === 0 ? <button type="button" style={btnPrimary} onClick={() => setRecording(true)}>Record a change</button> : undefined}
                />
              </div>
            ) : (
              <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={{ textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize }}>
                    {["Ref", "Person", "Change", "Effective", "Source", "Progress", "Status"].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {events.map((e) => {
                      const p = progress(e.items);
                      return (
                        <tr key={e.id} onClick={() => openEventById(e.id)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer" }}>
                          <td style={{ ...td, whiteSpace: "nowrap", color: theme.color.faint, fontWeight: 600 }}>{ref(e)}</td>
                          <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{e.personName}{e.personEmail && <div style={{ ...text.meta, fontWeight: 400 }}>{e.personEmail}</div>}</td>
                          <td style={td}><KindPill kind={e.kind} /></td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDateOnly(e.effectiveAt)}</td>
                          <td style={td}>{data.sources[e.source] ?? e.source}</td>
                          <td style={{ ...td, whiteSpace: "nowrap", color: p.done === p.total ? theme.color.successText : theme.color.muted }}>{p.done} / {p.total}</td>
                          <td style={td}><StatusPill value={e.status} label={e.status === "open" ? "Open" : undefined} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {openEvent && data && (
        <EventModal
          event={openEvent}
          canEdit={canEdit}
          canRevokeAccess={canRevokeAccess}
          sourceLabel={data.sources[openEvent.source] ?? openEvent.source}
          onClose={() => openEventById(null)}
          reload={load}
          onAmend={() => setAmending(openEvent)}
        />
      )}
      {amending && data && <RecordModal members={data.members} event={amending} onClose={() => setAmending(null)} onSaved={async () => { setAmending(null); await load(); }} />}
      {recording && data && <RecordModal members={data.members} onClose={() => setRecording(false)} onSaved={async (id) => { setRecording(false); await load(); openEventById(id); }} />}
      {importing && <ImportModal onClose={() => setImporting(false)} onImported={() => void load()} />}
      {webhook && data && <WebhookModal enabled={data.connections.hris} enabledAt={data.connections.hrisEnabledAt} orgId={orgId} onClose={() => setWebhook(false)} onChanged={() => void load()} />}
    </div>
  );
}

// ── Event detail ─────────────────────────────────────────────────────────────

function EventModal({ event, canEdit, canRevokeAccess, sourceLabel, onClose, reload, onAmend }: { event: LifecycleEvent; canEdit: boolean; canRevokeAccess: boolean; sourceLabel: string; onClose: () => void; reload: () => Promise<void>; onAmend: () => void }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  const d = event.detail ?? {};
  const isOpen = event.status === "open";
  const p = progress(event.items);
  const canRevoke = canRevokeAccess && isOpen && event.kind === "leaver" && !!event.userId && !!event.user?.isActive;

  const details: DetailField[] = [
    { label: "Person", value: event.personName },
    { label: "Email", value: event.personEmail },
    { label: "Change", value: <KindPill kind={event.kind} /> },
    { label: "Effective", value: fmtDateOnly(event.effectiveAt) },
    { label: "Source", value: sourceLabel },
    { label: "Platform account", value: event.user ? (event.user.isActive ? "Active" : "Deactivated") : "No platform account" },
  ];
  if (d.fromDepartment || d.toDepartment) details.push({ label: "Department", value: [d.fromDepartment, d.toDepartment].filter(Boolean).join(" to ") });
  if (d.fromJobTitle || d.toJobTitle) details.push({ label: "Job title", value: [d.fromJobTitle, d.toJobTitle].filter(Boolean).join(" to ") });
  if (d.fromSite || d.toSite) details.push({ label: "Office", value: [d.fromSite, d.toSite].filter(Boolean).join(" to ") });
  if (d.reason) details.push({ label: "Reason", value: d.reason, wide: true, multiline: true });
  if (event.status === "completed" && event.completedAt) details.push({ label: "Completed", value: fmtDateTime(event.completedAt) });
  if (event.status === "completed" && event.completedBy) details.push({ label: "Completed by", value: event.completedBy });
  if (event.status === "cancelled" && d.cancelledReason) details.push({ label: "Cancelled because", value: d.cancelledReason, wide: true, multiline: true });

  async function run(fn: () => Promise<unknown>, fallback: string) {
    setBusy(true); setError("");
    try { await fn(); await reload(); return true; }
    catch (e) { setError(apiErrorMessage(e, fallback)); return false; }
    finally { setBusy(false); }
  }

  async function tick(item: LifecycleItem) {
    const ok = await run(() => api.post(`/people/events/${event.id}/items/${encodeURIComponent(item.key)}`, { done: !item.done, note: note.trim() || null }), "Could not update the step.");
    if (ok) { setSelected(null); setNote(""); }
  }

  async function revoke() {
    if (!window.confirm(`Remove ${event.personName}'s platform access? Their account is deactivated and every session they have is ended.`)) return;
    await run(() => api.post(`/people/events/${event.id}/revoke-access`), "Could not remove their access.");
  }

  async function cancel() {
    if (reason.trim().length < 3) { setError("Say why the event is cancelled."); return; }
    const ok = await run(() => api.post(`/people/events/${event.id}/cancel`, { reason: reason.trim() }), "Could not cancel the event.");
    if (ok) { setCancelling(false); setReason(""); }
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={<span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{ref(event)} · {event.personName} <StatusPill value={event.status} label={event.status === "open" ? "Open" : undefined} /></span>}
        onClose={onClose}
      />
      <div style={{ padding: "8px 20px 20px" }}>
        <DetailSection title="Details">
          <DetailFields fields={details} />
        </DetailSection>

        <DetailSection title={`${KIND_LABEL[event.kind] ?? "Change"} checklist`} aside={<span style={text.hint}>{p.done} of {p.total} required steps done</span>}>
          <div style={{ ...text.hint, marginBottom: 8 }}>
            {canEdit && isOpen
              ? "Steps the platform checks tick themselves. To record a step you confirmed yourself, open it and mark it done; the tick is logged with your name."
              : "Steps the platform checks tick themselves; the others are ticked by an administrator once confirmed."}
          </div>
          <div style={{ border: "1px solid var(--c-border)", borderRadius: 3 }}>
            {event.items.map((item, i) => {
              const handTickable = !item.auto || !!item.manualBecause;
              const clickable = canEdit && isOpen && handTickable;
              const expanded = selected === item.key;
              return (
                <div key={item.key} style={{ borderTop: i ? "1px solid var(--c-border)" : undefined }}>
                  <div
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? () => { setSelected(expanded ? null : item.key); setNote(item.note ?? ""); setError(""); } : undefined}
                    onKeyDown={clickable ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setSelected(expanded ? null : item.key); setNote(item.note ?? ""); } } : undefined}
                    style={{ display: "flex", gap: 10, padding: "11px 14px", alignItems: "flex-start", cursor: clickable ? "pointer" : "default" }}
                  >
                    {item.done
                      ? <CheckCircle2 size={17} color={theme.color.success} style={{ flexShrink: 0, marginTop: 1 }} />
                      : <Circle size={17} color={theme.color.borderStrong} style={{ flexShrink: 0, marginTop: 1 }} />}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ ...text.body, fontWeight: 600 }}>
                        {item.title}
                        {!item.required && <span style={{ ...text.meta, fontWeight: 400 }}> · optional</span>}
                      </div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{item.detail}</div>
                      {item.auto && !item.manualBecause && (
                        <div style={{ ...text.meta, marginTop: 4, display: "flex", gap: 5, alignItems: "center" }}>
                          <ShieldCheck size={12} /> Checked by the platform{item.evidence ? ` — ${item.evidence}` : ""}
                        </div>
                      )}
                      {item.manualBecause && (
                        <div style={{ fontSize: text.meta.fontSize, color: theme.color.warningText, marginTop: 4 }}>Confirm by hand — {item.manualBecause}</div>
                      )}
                      {item.done && item.doneBy && item.doneBy !== "platform" && (
                        <div style={{ ...text.meta, marginTop: 4 }}>Done by {item.doneBy}{item.doneAt ? ` on ${fmtDateTime(item.doneAt)}` : ""}</div>
                      )}
                      {item.done && item.doneBy === "platform" && item.doneAt && (
                        <div style={{ ...text.meta, marginTop: 4 }}>Since {fmtDateTime(item.doneAt)}</div>
                      )}
                      {item.note && <div style={{ ...text.meta, marginTop: 4 }}>Note: {item.note}</div>}
                    </div>
                  </div>
                  {expanded && clickable && (
                    <div style={{ padding: "0 14px 12px 41px", display: "grid", gap: 8 }}>
                      <textarea style={{ ...textarea, minHeight: 50 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. what was checked and where" maxLength={1000} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" style={{ ...btnPrimary, padding: "6px 14px", opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void tick(item)}>{item.done ? "Mark as not done" : "Mark as done"}</button>
                        <button type="button" style={secondaryBtn} onClick={() => setSelected(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DetailSection>

        {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 10 }}>{error}</div>}

        {canEdit && isOpen && (
          <RecordActions>
            {cancelling ? (
              <div style={{ display: "grid", gap: 8, width: "100%" }}>
                <Field label="Why is this event cancelled?">
                  <textarea style={{ ...textarea, minHeight: 60 }} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="e.g. recorded twice, or the start was withdrawn" />
                </Field>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" style={{ ...dangerBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void cancel()}>Cancel this event</button>
                  <button type="button" style={secondaryBtn} onClick={() => { setCancelling(false); setReason(""); }}>Keep it</button>
                </div>
              </div>
            ) : (
              <>
                <button type="button" style={secondaryBtn} disabled={busy} onClick={onAmend}>Amend details</button>
                <button type="button" style={{ ...iconBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void run(() => api.post(`/people/events/${event.id}/refresh`), "Could not check again.")}><RefreshCw size={13} /> Check the automatic steps again</button>
                {canRevoke && <button type="button" style={{ ...dangerBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void revoke()}>Remove their platform access</button>}
                <button type="button" style={dangerBtn} onClick={() => { setCancelling(true); setError(""); }}>Cancel event</button>
              </>
            )}
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Record a change ──────────────────────────────────────────────────────────

const NO_ACCOUNT = "__none__";

// With `event`, the same form amends an open event (PATCH): who, when and what changed. The kind is fixed.
function RecordModal({ members, event, onClose, onSaved }: { members: Member[]; event?: LifecycleEvent; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const d0 = event?.detail ?? {};
  const [kind, setKind] = useState<Kind>(event?.kind ?? "joiner");
  const [memberId, setMemberId] = useState<string>(event?.userId ?? NO_ACCOUNT);
  const [name, setName] = useState(event && !event.userId ? event.personName : "");
  const [email, setEmail] = useState(event && !event.userId ? event.personEmail ?? "" : "");
  const [effective, setEffective] = useState(() => (event ? toDateInput(event.effectiveAt) : todayInput()));
  const [fromDepartment, setFromDepartment] = useState(d0.fromDepartment ?? "");
  const [toDepartment, setToDepartment] = useState(d0.toDepartment ?? "");
  const [fromJobTitle, setFromJobTitle] = useState(d0.fromJobTitle ?? "");
  const [toJobTitle, setToJobTitle] = useState(d0.toJobTitle ?? "");
  const [reason, setReason] = useState(d0.reason ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const noAccount = memberId === NO_ACCOUNT;

  async function save() {
    const effectiveAt = fromInput(effective);
    if (!effectiveAt) { setError("Give the date the change takes effect."); return; }
    if (noAccount && !name.trim()) { setError("Name the person, or choose a member."); return; }
    setBusy(true); setError("");
    try {
      const body = {
        userId: noAccount ? null : memberId,
        ...(noAccount ? { personName: name.trim(), personEmail: email.trim() || null } : {}),
        effectiveAt,
        detail: {
          fromDepartment: fromDepartment.trim() || null, toDepartment: toDepartment.trim() || null,
          fromJobTitle: fromJobTitle.trim() || null, toJobTitle: toJobTitle.trim() || null, reason: reason.trim() || null,
        },
      };
      if (event) {
        await api.patch(`/people/events/${event.id}`, body);
        await onSaved(event.id);
      } else {
        const r = await api.post<{ id: string }>("/people/events", { kind, ...body });
        await onSaved(r.id);
      }
    } catch (e) { setError(apiErrorMessage(e, event ? "Could not save the changes." : "Could not record the change.")); } finally { setBusy(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={event ? `Amend ${ref(event)}` : "Record a change"} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Field label="Change">
          <select style={input} value={kind} disabled={!!event} onChange={(e) => setKind(e.target.value as Kind)}>
            <option value="joiner">Joiner — starting</option>
            <option value="mover">Mover — changing role or team</option>
            <option value="leaver">Leaver — leaving</option>
          </select>
        </Field>
        <Field label="Effective date">
          <input style={input} type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
        </Field>
        <Field label="Person" span2 help="Choose a member with a platform account, or name someone without one.">
          <select style={input} value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value={NO_ACCOUNT}>Someone without a platform account</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.displayName || m.email}{m.displayName ? ` (${m.email})` : ""}{m.isActive ? "" : " — deactivated"}</option>)}
          </select>
        </Field>
        {noAccount && (
          <>
            <Field label="Name"><input style={input} value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Work email (optional)" help="Lets the platform find their directory account and devices."><input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </>
        )}
        <Field label="From department"><input style={input} value={fromDepartment} onChange={(e) => setFromDepartment(e.target.value)} /></Field>
        <Field label="To department"><input style={input} value={toDepartment} onChange={(e) => setToDepartment(e.target.value)} /></Field>
        <Field label="From job title"><input style={input} value={fromJobTitle} onChange={(e) => setFromJobTitle(e.target.value)} /></Field>
        <Field label="To job title"><input style={input} value={toJobTitle} onChange={(e) => setToJobTitle(e.target.value)} /></Field>
        <Field label="Reason (optional)" span2><textarea style={{ ...textarea, minHeight: 60 }} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Field>
        {error && <div style={{ ...errorText, gridColumn: "1 / -1", marginTop: 0 }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void save()}>{event ? "Save" : "Record"}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Import from HR ───────────────────────────────────────────────────────────

function ImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ created: number; existing: number; errors: string[] } | null>(null);

  function pick(file: File | undefined) {
    setResult(null); setError("");
    if (!file) { setCsv(null); setFileName(""); return; }
    if (file.size > 5 * 1024 * 1024) { setError("The file is larger than 5 MB."); return; }
    setFileName(file.name);
    file.text().then(setCsv).catch(() => setError("Could not read the file."));
  }

  async function upload() {
    if (!csv) return;
    setBusy(true); setError("");
    try {
      const r = await api.post<{ created: number; existing: number; errors: string[] }>("/people/import", { csv });
      setResult({ created: r.created, existing: r.existing, errors: r.errors ?? [] });
      onImported();
    } catch (e) { setError(apiErrorMessage(e, "Could not import the file.")); } finally { setBusy(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Import from HR" onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.hint, lineHeight: 1.6 }}>
          Upload an export from your HR system as a CSV file with a header row. The columns it needs:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            <li><strong>kind</strong> — joiner, mover or leaver (starter, leaver and transfer are read too)</li>
            <li><strong>email</strong> — their work email</li>
            <li><strong>name</strong> — their full name</li>
            <li><strong>effective date</strong> — the day the change takes effect</li>
            <li>Optional: <strong>id</strong> (the HR system's reference), <strong>department</strong>, <strong>job title</strong>, <strong>site</strong></li>
          </ul>
          <div style={{ marginTop: 6 }}>Importing the same file again adds nothing: each change is recorded once.</div>
        </div>
        <input type="file" accept=".csv,text/csv" onChange={(e) => pick(e.target.files?.[0])} style={text.body} />
        {fileName && csv != null && <div style={text.hint}>{fileName} · {(() => { const n = csv.split(/\r?\n/).filter(Boolean).length - 1; return `${n} ${n === 1 ? "row" : "rows"}`; })()}</div>}
        {result && (
          <div style={{ padding: 12, border: "1px solid var(--c-border)", borderRadius: 3, fontSize: text.body.fontSize, color: theme.color.text, display: "grid", gap: 4 }}>
            <div><strong>{result.created}</strong> new event{result.created === 1 ? "" : "s"} created</div>
            <div><strong>{result.existing}</strong> already recorded</div>
            <div><strong>{result.errors.length}</strong> line{result.errors.length === 1 ? "" : "s"} refused</div>
            {result.errors.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, ...text.hint, color: theme.color.dangerText, maxHeight: 180, overflowY: "auto" }}>
                {result.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
        {error && <div style={{ ...errorText, marginTop: 0 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>{result ? "Close" : "Cancel"}</button>
          {!result && <button type="button" style={{ ...btnPrimary, opacity: busy || !csv ? 0.6 : 1 }} disabled={busy || !csv} onClick={() => void upload()}>Import</button>}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── HR system webhook ────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const EXAMPLE_BODY = `{
  "events": [
    {
      "id": "HR-12345",
      "kind": "leaver",
      "email": "sam.jones@example.com",
      "name": "Sam Jones",
      "effectiveAt": "2026-10-01T17:00:00Z",
      "department": "Finance",
      "previousDepartment": null,
      "jobTitle": "Accountant",
      "previousJobTitle": null,
      "site": "London"
    }
  ]
}`;

function WebhookModal({ enabled, enabledAt, orgId, onClose, onChanged }: { enabled: boolean; enabledAt: string | null; orgId: string | null; onClose: () => void; onChanged: () => void }) {
  const [issued, setIssued] = useState<{ secret: string; endpoint: string } | null>(null);
  const [on, setOn] = useState(enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState<"" | "secret" | "endpoint">("");

  const endpointPath = issued?.endpoint ?? (orgId ? `/people/hris/${orgId}/events` : null);
  const endpoint = endpointPath ? `${API_BASE}${endpointPath}` : null;

  async function issue() {
    if (on && !window.confirm("Issue a new secret? The current one stops working at once, so your HR system must be updated with the new one.")) return;
    setBusy(true); setError("");
    try {
      const r = await api.post<{ secret: string; endpoint: string }>("/people/hris/secret");
      setIssued({ secret: r.secret, endpoint: r.endpoint }); setOn(true); onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Could not issue a secret.")); } finally { setBusy(false); }
  }

  async function switchOff() {
    if (!window.confirm("Switch off the HR system webhook? Its secret is deleted and anything it sends is refused.")) return;
    setBusy(true); setError("");
    try { await api.del("/people/hris/secret"); setIssued(null); setOn(false); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "Could not switch it off.")); } finally { setBusy(false); }
  }

  function copy(what: "secret" | "endpoint", value: string) {
    navigator.clipboard?.writeText(value).then(() => setCopied(what)).catch(() => {});
  }

  const code: React.CSSProperties = {
    display: "block", padding: "6px 8px", background: "var(--c-panelAlt)", border: `1px solid ${theme.color.border}`,
    borderRadius: 3, ...text.mono, color: theme.color.heading, wordBreak: "break-all",
  };

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={<span style={{ display: "flex", gap: 8, alignItems: "center" }}><Webhook size={16} /> HR system webhook <OnOff on={on} /></span>} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14, ...text.body }}>
        <div style={{ ...text.hint, lineHeight: 1.6 }}>
          Your HR system can send starts, moves and departures here as they happen. Each request is
          signed with a secret only you and the HR system hold, so nobody else can add events. It
          can only ever create events; it never changes an account.
          {on && enabledAt && !issued && <> Switched on {fmtDateTime(enabledAt)}.</>}
        </div>

        {endpoint && (
          <Field label="Endpoint (POST)">
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ ...code, flex: 1, minWidth: 0 }}>{endpoint}</code>
              <button type="button" style={iconBtn} onClick={() => copy("endpoint", endpoint)}>{copied === "endpoint" ? <CheckIcon size={13} /> : <Copy size={13} />}{copied === "endpoint" ? "Copied" : "Copy"}</button>
            </div>
          </Field>
        )}

        {issued && (
          <div style={{ padding: 12, background: "var(--c-warningBg)", border: `1px solid ${theme.color.warning}`, borderRadius: 3 }}>
            <div style={{ ...text.itemTitle, color: theme.color.warningText, marginBottom: 4 }}>Shown once — copy it now</div>
            <div style={{ ...text.hint, color: theme.color.warningText, marginBottom: 8 }}>
              Put this secret in your HR system. It replaces any previous secret, which has stopped working. It cannot be shown again; if it is lost, issue another.
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ ...code, flex: 1, minWidth: 0, fontSize: text.body.fontSize, fontWeight: 700, background: "var(--c-panel)" }}>{issued.secret}</code>
              <button type="button" style={iconBtn} onClick={() => copy("secret", issued.secret)}>{copied === "secret" ? <CheckIcon size={13} /> : <Copy size={13} />}{copied === "secret" ? "Copied" : "Copy"}</button>
            </div>
          </div>
        )}

        <div>
          <div style={{ fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>How to sign a request</div>
          <ul style={{ margin: 0, paddingLeft: 18, ...text.hint, lineHeight: 1.7 }}>
            <li>Header <strong>x-abacas-timestamp</strong>: the time now, in Unix seconds. Requests more than five minutes out are refused, so a captured request cannot be replayed later.</li>
            <li>Header <strong>x-abacas-signature</strong>: <strong>sha256=</strong> followed by the hex HMAC-SHA256, under the secret, of the timestamp, a full stop, then the raw request body exactly as sent.</li>
            <li>Body: JSON with an <strong>events</strong> list (up to 500). Each event needs <strong>id</strong>, <strong>kind</strong> (joiner, mover or leaver), <strong>email</strong>, <strong>name</strong> and <strong>effectiveAt</strong> (ISO date and time with offset); <strong>department</strong>, <strong>previousDepartment</strong>, <strong>jobTitle</strong>, <strong>previousJobTitle</strong> and <strong>site</strong> are optional.</li>
            <li>Sending the same event again adds nothing: the id, kind and date make it unique.</li>
          </ul>
          <pre style={{ ...code, marginTop: 8, whiteSpace: "pre-wrap", overflowX: "auto", fontSize: text.meta.fontSize, fontWeight: 400 }}>{EXAMPLE_BODY}</pre>
        </div>

        {error && <div style={{ ...errorText, marginTop: 0 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {on && <button type="button" style={{ ...dangerBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void switchOff()}>Switch off</button>}
          <button type="button" style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => void issue()}>{on ? "Issue a new secret" : "Switch on and issue a secret"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </ModalOverlay>
  );
}
